/**
 * Centralized storage key constants.
 * LS_ prefix = localStorage (persistent). SS_ prefix = sessionStorage (per-session).
 * All keys use `lev/<namespace>/<key>` naming convention.
 */

// localStorage — persistent across sessions
export const LS_COLOR_SCHEME = "lev/preferences/color-scheme";
export const LS_TUTORIAL_ASK_LEVAI = "lev/tutorial/ask-levai";
export const LS_TUTORIAL_VERSION_HISTORY = "lev/tutorial/version-history";
export const LS_TUTORIAL_APPROVE_BUTTON = "lev/tutorial/approve-button";
export const LS_TUTORIAL_EDIT_BUTTON = "lev/tutorial/edit-button";
export const LS_TUTORIAL_COPY_BUTTON = "lev/tutorial/copy-button";
export const LS_TUTORIAL_DOWNLOAD_BUTTON = "lev/tutorial/download-button";
export const LS_TUTORIAL_CREATE_ALL_BIG_IDEA_OPENED =
  "lev/tutorial/create-all-big-idea-opened";
/**
 * Per-pipeline-kind guided tour keys. `${LS_TUTORIAL_PIPELINE_TOUR_PREFIX}/<kind>/auto-starts`
 * holds how many times the tour has auto-started ("0", "1", "2"); it
 * auto-starts at most twice, and "Take the tour" never counts.
 * `${LS_TUTORIAL_PIPELINE_TOUR_PREFIX}/<kind>` is the old once-only "seen"
 * marker, read only to migrate a browser that has it to a count of 1 (see
 * useTour). Scoped by kind, not by project, so a founder does not get the
 * tour replayed on every new project.
 */
export const LS_TUTORIAL_PIPELINE_TOUR_PREFIX = "lev/tutorial/pipeline-tour";
/**
 * "Seen the Overall Evaluation tour" marker. Per browser, not per project, so
 * the tour auto-starts once and later projects only offer "Take the tour".
 */
export const LS_TUTORIAL_EVALUATION_TOUR = "lev/tutorial/evaluation-tour";
export const LS_EVALUATION_RESULT_CACHE_PREFIX = "lev/evaluation/cache";
export const LS_SHARE_WELCOME_DISMISSED = "lev/share/welcome-dismissed";
export const LS_COLLABORATE_WELCOME_DISMISSED = "lev/collaborate/welcome-dismissed";
export const LS_YC_BANNER_DISMISSED = "lev/yc/banner-dismissed";
/** Last project opened in the mobile shell — powers /m's project-less entry redirect. */
export const LS_MOBILE_LAST_PROJECT_ID = "lev/mobile/last-project-id";
/** "1" once the founder has opted out of the phone → mobile-app redirect. */
export const LS_MOBILE_PREFERS_DESKTOP = "lev/mobile/prefers-desktop";

/**
 * Per-(projectId, pipeline, stageKey) kanban column collapse state.
 * Full key shape: `${LS_PIPELINE_KANBAN_COLLAPSED_PREFIX}/${projectId}/${pipeline}/${stageKey}` → "1" | "0".
 */
export const LS_PIPELINE_KANBAN_COLLAPSED_PREFIX = "lev/pipeline-kanban/collapsed";
/**
 * Per-view column (attribute) visibility for the Orbit + pipeline list views.
 * Full key shape: `${LS_ORBIT_COLUMN_VISIBILITY_PREFIX}/${viewId}` → JSON map of
 * `{ [columnId]: boolean }`. `viewId` namespaces People / Companies / each
 * pipeline so each surface keeps its own show/hide choices (LEV-1019).
 */
export const LS_ORBIT_COLUMN_VISIBILITY_PREFIX = "lev/orbit/column-visibility";
/**
 * Per-view property (column/attribute) ORDER for the Orbit + pipeline list
 * views. Full key shape: `${LS_ORBIT_COLUMN_ORDER_PREFIX}/${viewId}` → JSON
 * array of column ids in the user's drag-chosen order. Required (pinned)
 * columns are kept first regardless of the stored order; unknown/new ids fall
 * back to registry order (LEV-1080).
 */
export const LS_ORBIT_COLUMN_ORDER_PREFIX = "lev/orbit/column-order";
/**
 * Per-(projectId, section) open/collapsed state for the sidebar task list
 * accordions (Need your approval / In progress / Suggested / Completed).
 * Full key shape: `${LS_TASK_SECTION_OPEN_PREFIX}/${projectId}/${sectionKey}` → "1" (open) | "0" (collapsed).
 * No stored value → the section's `defaultOpen` applies.
 */
export const LS_TASK_SECTION_OPEN_PREFIX = "lev/task-list/section-open";
/**
 * Per-(projectId, dimension column, status bucket) open/collapsed state for the
 * task-kanban By Dimension board's accordion sections.
 * Full key shape: `${LS_TASK_KANBAN_DIMENSION_SECTION_OPEN_PREFIX}/${projectId}/${columnKey}/${bucket}` → "1" | "0".
 */
export const LS_TASK_KANBAN_DIMENSION_SECTION_OPEN_PREFIX = "lev/task-kanban/dimension-section-open";
/**
 * T.A.'s chosen minimum shared-company count for neighbor firm candidates in
 * the Venture Graph review queue. Stores a stringified integer; missing or
 * unparseable falls back to the default (LEV-1911).
 */
export const LS_VENTURE_QUEUE_NEIGHBOR_THRESHOLD = "lev/venture-review/neighbor-threshold";
/**
 * The Queue sub-tab (a review kind) last opened in the Venture Graph review
 * tool. Missing or unknown falls back to the firm-candidate sub-tab (LEV-1923).
 */
export const LS_VENTURE_QUEUE_SUB_TAB = "lev/venture-review/queue-sub-tab";
/**
 * The fund-size chip on the Venture Graph review queue's firm candidates
 * ("small", "inBand" or "any"). Missing or unknown falls back to "inBand" (LEV-1989).
 */
export const LS_VENTURE_QUEUE_SIZE_BAND = "lev/venture-review/size-band";
/** Onboarding facts inspector (flag-gated, signed-in viewers): "1" (open) | "0" (collapsed). */
export const LS_DEV_FACTS_INSPECTOR_OPEN = "lev/dev/facts-inspector-open";
/**
 * Per-application Brutal read card collapse state (LEV-2511).
 * Full key shape: `${LS_BRUTAL_VERDICT_COLLAPSED_PREFIX}/${appId}` → "1" (collapsed) | "0" (open).
 * No stored value defaults open, so the card opens the first time scores
 * land; only an explicit founder toggle is ever persisted.
 */
export const LS_BRUTAL_VERDICT_COLLAPSED_PREFIX = "lev/brutal-verdict/collapsed";
/**
 * Per-application "Brutal read" LABEL disclosure (LEV-2643): collapsing it
 * hides the verdict and the reasons together, distinct from the reasons-only
 * toggle above. Same shape as LS_BRUTAL_VERDICT_COLLAPSED_PREFIX
 * (`${prefix}/${appId}` → "1" (collapsed) | "0" (open)), but opposite
 * default: no stored value opens the read, so a founder sees it the first
 * time scores land.
 */
export const LS_BRUTAL_READ_COLLAPSED_PREFIX = "lev/brutal-read/collapsed";

// sessionStorage — per-session
export const SS_SESSION_EXPIRED = "lev/analytics/session-expired";
export const SS_LAST_AUTH_FLOW = "lev/analytics/last-auth-flow";
// Guards against re-sending user_first_session_started on navigation within the
// 10-minute new-account window. Stores the user id; presence + match means sent.
export const SS_FIRST_SESSION_SENT = "lev/analytics/first-session-sent";
export const SS_PENDING_IDEA = "lev/onboarding/pending-idea";
export const SS_PENDING_IDEA_BACKUP = "lev/onboarding/pending-idea-backup";
export const SS_ONBOARDING_CHAT = "lev/onboarding/chat";
export const SS_ONBOARDING_CHAT_BACKUP = "lev/onboarding/chat-backup";
/**
 * Full onboarding session snapshot (result + messages + materials), kept as the
 * conversion fallback when the server session cookie is unavailable at claim
 * time. Written on every onboarding update and at the build/skip handoff;
 * cleared only after a project is successfully created. See lost-idea fix.
 */
export const SS_ONBOARDING_SESSION_BACKUP = "lev/onboarding/session-backup";
export const SS_REFERRAL_ID = "lev/referral/id";
export const SS_LINKEDIN_POSTS = "lev/labs/linkedin-posts";
/** Share /p/[token] canvas: one-time comment-mode hint per browser tab session */
export const SS_SHARE_COMMENT_PLACEMENT_HINT_DISMISSED =
  "lev/share/comment-placement-hint-dismissed";
/**
 * Terms this visitor has already asked Lev Learn to write, so one person
 * pressing the button on three concept pages counts as one vote rather than
 * three. Session-scoped rather than a `localStorage` key on purpose: the digest
 * ranks by how many people asked, and a visit is the closest honest proxy for a
 * person we have without identifying anyone. A genuine re-ask weeks later is
 * new signal and should count again.
 */
export const SS_LEARN_REQUESTED_TERMS = "lev/learn/requested-terms";
/**
 * Raw onboarding session token stashed client-side after a successful POST mint.
 * Carried in the sign-up redirect URL (`?oc=<token>`) so it survives cross-browser-
 * context handoffs (e.g. email-verification link opened in Gmail's in-app browser).
 * The server still stores only the SHA-256 hash. See ADR 041.
 */
export const SS_ONBOARDING_CLAIM_TOKEN = "lev/onboarding/claim-token";
/**
 * One-shot marker: a founder with an unsent chat should have it auto-restored,
 * not claimed, on landing back from sign-in. Set on `/onboarding/complete`,
 * after sign-in, right before the redirect back — not before the sign-in
 * redirect. `peekResumeChat` treats it as expired after 10 minutes so a stale
 * marker from a long-abandoned tab can't reopen chat days later (LEV-1925).
 */
export const SS_ONBOARDING_RESUME_CHAT = "lev/onboarding/resume-chat";
/**
 * Per-(Clerk user id) session fallback for the Accelerators announcement
 * banner's dismissal. Full key shape:
 * `${SS_ACCELERATORS_ANNOUNCEMENT_DISMISSED_PREFIX}/${clerkUserId}` → "1".
 * Clerk `unsafeMetadata` is the durable dismissal; this is a tab-session
 * backstop for when that write fails, so remounting the page (opening a
 * project and coming back) does not show the banner again before the next
 * successful write.
 */
export const SS_ACCELERATORS_ANNOUNCEMENT_DISMISSED_PREFIX = "lev/accelerators/announcement-dismissed";
